import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { AlertCircle, CheckCircle, Loader2, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import BirdAvatar, { BIRD_AVATARS } from './BirdAvatar';
import useGameStore from '../store/gameStore';

const NicknameSetup = ({ onComplete }) => {
  const { setNickname: setStoreNickname, setAvatarId: setStoreAvatarId } = useGameStore();
  const [nickname, setNickname] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(1);
  const [step, setStep] = useState(1); // 1: nickname, 2: avatar selection
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState('');
  const [isAvailable, setIsAvailable] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const validateNickname = (value) => {
    // Remove extra spaces
    value = value.trim();
    
    if (value.length < 3) {
      return 'Nickname must be at least 3 characters';
    }
    if (value.length > 20) {
      return 'Nickname must be at most 20 characters';
    }
    if (!/^[a-zA-Z0-9_\- ]+$/.test(value)) {
      return 'Only letters, numbers, spaces, hyphens, and underscores allowed';
    }
    
    // Basic profanity check (backend has more comprehensive list)
    const badWords = ['fuck', 'shit', 'ass', 'bitch', 'damn', 'bastard', 'dick', 'cock', 'pussy', 'cunt', 'whore', 'slut'];
    const lowerValue = value.toLowerCase();
    for (const word of badWords) {
      if (lowerValue.includes(word)) {
        return 'This nickname contains inappropriate language';
      }
    }
    
    return null;
  };

  const checkNickname = async () => {
    setError('');
    setIsAvailable(false);
    setHasChecked(false);
    
    const validationError = validateNickname(nickname);
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setIsChecking(true);
    
    try {
      const response = await fetch(`${backendUrl}/api/players/check-nickname`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ nickname: nickname.trim() }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsAvailable(true);
        setError('');
      } else {
        setError(data.message);
        setIsAvailable(false);
      }
      
      setHasChecked(true);
    } catch (err) {
      setError('Could not check nickname. Please try again.');
      console.error('Error checking nickname:', err);
    } finally {
      setIsChecking(false);
    }
  };

  const registerNickname = async () => {
    if (!isAvailable) return;
    
    setIsChecking(true);
    
    try {
      const response = await fetch(`${backendUrl}/api/players/register-nickname`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          nickname: nickname.trim(),
          avatar_id: selectedAvatar 
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Store in localStorage
        localStorage.setItem('jailbirds_nickname', data.nickname);
        localStorage.setItem('jailbirds_player_id', data.player_id);
        localStorage.setItem('jailbirds_avatar_id', selectedAvatar.toString());
        
        // Update the game store immediately
        setStoreNickname(data.nickname);
        setStoreAvatarId(selectedAvatar);
        
        onComplete(data.nickname, selectedAvatar);
      } else {
        setError(data.message);
        setIsAvailable(false);
        setHasChecked(false);
      }
    } catch (err) {
      setError('Could not register nickname. Please try again.');
      console.error('Error registering nickname:', err);
    } finally {
      setIsChecking(false);
    }
  };

  const handleInputChange = (e) => {
    setNickname(e.target.value);
    setError('');
    setIsAvailable(false);
    setHasChecked(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (isAvailable) {
        registerNickname();
      } else {
        checkNickname();
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-background via-background/95 to-card/50 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: 'spring', duration: 0.6 }}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <Card className="p-6 sm:p-8 bg-gradient-card border-border shadow-2xl">
          <AnimatePresence mode="wait">
            {step === 1 ? (
              // Step 1: Nickname Selection
              <motion.div
                key="nickname-step"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                {/* Title */}
                <div className="text-center mb-6">
                  <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-2">
                    Choose Your <span className="text-gradient-primary">Nickname</span>
                  </h2>
                  <p className="text-sm sm:text-base text-muted-foreground">
                    This will be your identity on the leaderboard
                  </p>
                </div>

                {/* Input */}
                <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                  <div>
                    <Input
                      type="text"
                      placeholder="Enter your nickname"
                      value={nickname}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      disabled={isChecking}
                      className="text-base sm:text-lg h-11 sm:h-12"
                      maxLength={20}
                      autoFocus
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      3-20 characters • Letters, numbers, spaces, hyphens, underscores
                    </p>
                  </div>

                  {/* Status Messages */}
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 text-destructive text-xs sm:text-sm"
                    >
                      <AlertCircle className="w-4 h-4" />
                      <span>{error}</span>
                    </motion.div>
                  )}
                  
                  {isAvailable && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 text-success text-xs sm:text-sm"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Available! Click next to choose your avatar.</span>
                    </motion.div>
                  )}
                </div>

                {/* Buttons */}
                <div className="flex flex-col gap-3">
                  {!isAvailable ? (
                    <Button
                      size="lg"
                      onClick={checkNickname}
                      disabled={isChecking || nickname.trim().length < 3}
                      className="w-full bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold text-sm sm:text-base"
                    >
                      {isChecking ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Checking...
                        </>
                      ) : (
                        <>
                          Check Availability
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      size="lg"
                      onClick={() => setStep(2)}
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-semibold shadow-lg text-sm sm:text-base"
                    >
                      Next: Choose Your Avatar
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>

                {/* Additional Info */}
                <div className="mt-4 sm:mt-6 text-center space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Your scores will be tracked daily, weekly, and monthly.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    You can change your nickname and avatar at anytime through the link at the bottom of the homepage.
                  </p>
                </div>
              </motion.div>
            ) : (
              // Step 2: Avatar Selection
              <motion.div
                key="avatar-step"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                {/* Title */}
                <div className="text-center mb-4 sm:mb-6">
                  <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-2">
                    Choose Your <span className="text-gradient-primary">Avatar</span>
                  </h2>
                  <p className="text-sm sm:text-base text-muted-foreground">
                    Pick a bird that represents you!
                  </p>
                </div>

                {/* Selected Avatar Preview */}
                <div className="flex flex-col items-center mb-4 sm:mb-6">
                  <BirdAvatar avatarId={selectedAvatar} size={100} animate={true} />
                  <p className="mt-3 text-base sm:text-lg font-semibold text-primary">
                    {BIRD_AVATARS.find(a => a.id === selectedAvatar)?.name}
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {nickname}
                  </p>
                </div>

                {/* Avatar Grid */}
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 sm:gap-3 mb-4 sm:mb-6 max-h-[200px] sm:max-h-[280px] overflow-y-auto p-2 rounded-lg border border-border">
                  {BIRD_AVATARS.map((avatar) => (
                    <motion.button
                      key={avatar.id}
                      onClick={() => setSelectedAvatar(avatar.id)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`p-2 sm:p-3 rounded-lg border-2 transition-all ${
                        selectedAvatar === avatar.id
                          ? 'border-primary bg-primary/10 shadow-lg'
                          : 'border-border bg-card hover:border-primary/50'
                      }`}
                    >
                      <BirdAvatar avatarId={avatar.id} size={50} animate={false} />
                      <p className="text-xs mt-1 text-muted-foreground truncate">{avatar.name}</p>
                    </motion.button>
                  ))}
                </div>

                {/* Buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setStep(1);
                      setError('');
                      setIsAvailable(false);
                      setHasChecked(false);
                    }}
                    disabled={isChecking}
                    className="w-24 sm:w-32 text-sm sm:text-base"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  
                  <Button
                    size="lg"
                    onClick={registerNickname}
                    disabled={isChecking}
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-semibold shadow-lg text-sm sm:text-base"
                  >
                    {isChecking ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Start Playing
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>
      </motion.div>
    </motion.div>
  );
};

export default NicknameSetup;
